import { useState } from "react";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import { PageHeader } from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { IMAGES } from "@/lib/images";
import { submitBooking } from "@/lib/api";

const EVENT_TYPES = [
  "At-Home Concert",
  "Church / Faith Gathering",
  "Senior Living Community",
  "Christmas Concert / Christmas Program",
  "Patriotic / Veterans / Remembrance Program",
  "Wedding / Reception",
  "Anniversary / Vow Renewal",
  "Social Gathering / Family Celebration",
  "Other",
];

const AUDIENCE_SIZES = ["Under 50 guests", "50 - 150 guests", "150 - 300 guests", "300+ guests"];

const INITIAL = {
  full_name: "",
  organization: "",
  email: "",
  phone: "",
  event_date: "",
  location: "",
  event_type: "",
  audience_size: "",
  message: "",
};

const fieldClass =
  "w-full border border-white/10 bg-charcoal-900/80 px-4 py-3 text-sm font-light text-sand placeholder:text-mist/50 focus:border-gold/60 focus:outline-none focus:ring-1 focus:ring-gold/40 transition-colors duration-300";

const Field = ({ label, htmlFor, required, children }) => (
  <div>
    <label htmlFor={htmlFor} className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-mist">
      {label} {required && <span className="text-gold">*</span>}
    </label>
    {children}
  </div>
);

export default function Booking() {
  const [form, setForm] = useState(INITIAL);
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await submitBooking(form);
      toast.success("Booking inquiry received", {
        description: "Thank you — Derrick will respond to your inquiry personally.",
      });
      setForm(INITIAL);
    } catch {
      toast.error("Something went wrong", { description: "Please try again in a moment." });
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Seo
        title="Booking — Derrick Hall"
        siteName="Derrick Hall"
        description="Request booking information for Derrick Hall, singer and vocalist, for your event."
      />
      <PageHeader
        testId="booking-header"
        eyebrow="Booking"
        title="Request Booking Information"
        description="An at-home concert, a church program, music for a senior living community, or another meaningful gathering — share the details below. No rates are published; every inquiry receives a personal response."
      />

      <div className="mx-auto grid max-w-7xl gap-12 px-6 pb-28 lg:grid-cols-[1fr_22rem]">
        <Reveal>
          <form onSubmit={onSubmit} data-testid="booking-form" className="grid gap-6 sm:grid-cols-2">
            <Field label="Name" htmlFor="full_name" required>
              <Input id="full_name" data-testid="booking-input-name" required value={form.full_name} onChange={set("full_name")} className={fieldClass} placeholder="Your full name" />
            </Field>
            <Field label="Organization / Event" htmlFor="organization">
              <Input id="organization" data-testid="booking-input-org" value={form.organization} onChange={set("organization")} className={fieldClass} placeholder="Organization or event name" />
            </Field>
            <Field label="Email" htmlFor="email" required>
              <Input id="email" type="email" data-testid="booking-input-email" required value={form.email} onChange={set("email")} className={fieldClass} placeholder="you@example.com" />
            </Field>
            <Field label="Phone" htmlFor="phone">
              <Input id="phone" type="tel" data-testid="booking-input-phone" value={form.phone} onChange={set("phone")} className={fieldClass} placeholder="(optional)" />
            </Field>
            <Field label="Event Date" htmlFor="event_date" required>
              <Input id="event_date" type="date" data-testid="booking-input-date" required value={form.event_date} onChange={set("event_date")} className={fieldClass} />
            </Field>
            <Field label="Location" htmlFor="location" required>
              <Input id="location" data-testid="booking-input-location" required value={form.location} onChange={set("location")} className={fieldClass} placeholder="City, State / Venue" />
            </Field>
            <Field label="Type of Event" htmlFor="event_type" required>
              <select id="event_type" data-testid="booking-select-event-type" required value={form.event_type} onChange={set("event_type")} className={fieldClass}>
                <option value="" disabled>Select a type</option>
                {EVENT_TYPES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </Field>
            <Field label="Estimated Audience Size" htmlFor="audience_size" required>
              <select id="audience_size" data-testid="booking-select-audience" required value={form.audience_size} onChange={set("audience_size")} className={fieldClass}>
                <option value="" disabled>Select a range</option>
                {AUDIENCE_SIZES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </Field>
            <div className="sm:col-span-2">
              <Field label="Message" htmlFor="message" required>
                <Textarea id="message" data-testid="booking-input-message" required rows={6} value={form.message} onChange={set("message")} className={fieldClass} placeholder="Tell us about your event — the occasion, the atmosphere, and anything else that would help." />
              </Field>
            </div>
            <div className="sm:col-span-2">
              <button
                type="submit"
                data-testid="booking-submit-btn"
                disabled={sending}
                className="bg-gold px-8 py-4 font-mono text-[11px] uppercase tracking-[0.2em] text-charcoal-950 transition-colors duration-300 hover:bg-gold-light disabled:cursor-not-allowed disabled:opacity-50"
              >
                {sending ? "Sending…" : "Request Booking Information"}
              </button>
            </div>
          </form>
        </Reveal>

        <Reveal delay={0.15}>
          <aside data-testid="booking-aside" className="space-y-6">
            <div className="relative">
              <div className="absolute -right-4 -top-4 h-full w-full border border-gold/25" />
              <img
                src={IMAGES.micStand}
                alt="Microphone on stage in warm light"
                className="relative aspect-[4/5] w-full object-cover"
              />
            </div>
            <div className="border border-white/[0.08] bg-charcoal-900/60 p-6">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">The format</p>
              <p className="mt-3 text-sm font-light leading-relaxed text-mist" data-testid="booking-format-note">
                Derrick performs live vocals with professionally produced instrumental
                accompaniment, creating a full concert experience that can be brought into
                intimate and flexible settings.
              </p>
            </div>
            <div className="border border-white/[0.08] bg-charcoal-900/60 p-6">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">A personal reply</p>
              <p className="mt-3 text-sm font-light leading-relaxed text-mist">
                Every inquiry is read and answered personally. Include as much detail as
                you can — it helps shape the right musical fit for your event.
              </p>
            </div>
          </aside>
        </Reveal>
      </div>
    </>
  );
}
